import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslationLoaderService } from './translation-loader.service';
import { translateDynamic } from './dynamic-translations';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private defaultLang = 'en';
  private currentLang$ = new BehaviorSubject<string>(this.defaultLang);
  private supportedLangs = ['en', 'es'];

  constructor(
    private translate: TranslateService,
    private translationLoader: TranslationLoaderService
  ) {
    this.translateToasts();

    // Load language from localStorage, fallback to default
    const storedLang = localStorage.getItem('lang') || this.defaultLang;
    // Validate stored language is supported
    const validLang = this.supportedLangs.includes(storedLang) ? storedLang : this.defaultLang;
    this.setLanguage(validLang);
  }

  /**
   * Toasts are fired from ~300 places with plain English text. Translate their
   * title/message here instead, so every toast follows the selected language.
   * Text with no translation (or built dynamically) is shown as written.
   */
  private translateToasts(): void {
    const toastr = inject(ToastrService);
    const t = (text?: string) => (text ? this.instant(text) : text);
    for (const method of ['success', 'error', 'warning', 'info', 'show'] as const) {
      const original = (toastr as any)[method].bind(toastr);
      (toastr as any)[method] = (message?: string, title?: string, override?: any, ...rest: any[]) =>
        original(t(message), t(title), override, ...rest);
    }
  }

  /** Translate a text, falling back to the text itself when there is no translation. */
  instant(text: string): string {
    if (!text) return text;
    const translated = this.translate.instant(text);
    if (translated !== text) return translated;
    // Messages built in code with a value inside (see dynamic-translations.ts)
    return translateDynamic(text, this.getCurrentLang(), (v) => this.translate.instant(v)) ?? text;
  }

  setLanguage(lang: string): void {
    // Only set if language is supported
    if (!this.supportedLangs.includes(lang)) {
      console.warn(`Language ${lang} is not supported. Falling back to ${this.defaultLang}`);
      lang = this.defaultLang;
    }

    // Load all of the language's files first so no screen flashes untranslated text
    this.translationLoader.loadAll(lang).pipe(
      switchMap(() => this.translate.use(lang))
    ).subscribe(() => {
      localStorage.setItem('lang', lang);
      this.currentLang$.next(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = 'ltr';
    });
  }

  getCurrentLang(): string {
    return this.translate.currentLang || this.defaultLang;
  }

  getCurrentLang$(): Observable<string> {
    return this.currentLang$.asObservable();
  }

  toggleLanguage(): void {
    const current = this.getCurrentLang();
    const newLang = current === 'en' ? 'es' : 'en';
    this.setLanguage(newLang);
  }

  getSupportedLanguages(): string[] {
    return [...this.supportedLangs];
  }

  isLanguageSupported(lang: string): boolean {
    return this.supportedLangs.includes(lang);
  }
}
